// src/modules/authorized-pickup-persons/pages/AuthorizedPickUpPersonPage.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import PageHeader from "components/Common/PageHeader";
import { all_routes } from "router/all_routes";
import CustomButton from "components/Common/ui/CustomButton";
import TableFilterDynamique, { FilterField } from "components/Common/TableFilter/TableFilterDynamique";
import useAuthorizedPickUpPerson from "../hooks/useAuthorizedPickUpPerson";
import AuthorizedPickUpPersonTable from "../components/AuthorizedPickUpPersonTable";
import AddEditAuthorizedPickUpPerson from "../components/AddEditAuthorizedPickUpPerson";
import { RootState } from "redux/store";
import CustomAlert from "components/Common/ui/CustomAlert";
import { useEffectiveStudentId } from "components/Peoples/guardian/hooks/useEffectiveStudentId";
import StudentSelectorWrapper from "components/Peoples/guardian/StudentSelectorWrapper";

const AuthorizedPickUpPersonPage: React.FC = () => {
    const { t } = useTranslation();
    const {
        authorizedPickUpPersons,
        loading,
        infoPagination,
        setIsModalOpen,
        isModalOpen,
        handleCreate,
        handleUpdate,
        handleDelete,
        relationshipOptions,
        selectedPickUpPerson,
        setSelectedPickUpPerson,
        resetSelection,
        fetchByStudentId,
    } = useAuthorizedPickUpPerson();

    // const user = useSelector((state: RootState) => state.authSlice.user);
    const [filters, setFilters] = useState<{ [key: string]: string | number | null }>({});

    const breadcrumbItems = [
        { label: "dashboard", path: all_routes.dashboard },
        { label: "authorizedPickUpPerson.title", active: true },
    ];
    const studentId = useEffectiveStudentId();
    const guardianId = useSelector((state: any) => state.authSlice.user?.roleId);

    const filterFields: FilterField[] = useMemo(
        () => [
            { key: "search", label: t("search"), type: "text" },
            { key: "relationship", label: t("authorizedPickUpPerson.form.relationship"), type: "select", options: relationshipOptions },
        ],
        [t, relationshipOptions]
    );


    const handleSave = async (payload: any) => {
        try {
            let result = false;
            const finalPayload = { ...payload, studentId: studentId, guardianId: guardianId };

            if (selectedPickUpPerson?.id) {
                result = await handleUpdate(selectedPickUpPerson.id, finalPayload);
            } else {
                result = await handleCreate(finalPayload);
            }

            if (result) {
                resetSelection();
                refetchData();
            }
        } catch (error) {
            console.error("Error saving authorized pick-up person:", error);
        }
    };

    const handleFilter = (newFilters: { [key: string]: any }) => {
        setFilters(newFilters);
        refetchData(newFilters);
    };

    const refetchData = (currentFilters = filters) => {
        const params = {
            ...currentFilters,
            page: 1,
            limit: 10,
        };

        fetchByStudentId(studentId, params);

    };

    const handleEdit = (record: any) => {
        setSelectedPickUpPerson(record);
        setIsModalOpen(true);
    };

    const handleAddNew = () => {
        setSelectedPickUpPerson(null);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        resetSelection();
    };
    const handleDeletePickUpPerson = async (id: string) => {
        const result = await handleDelete(id);
        if (result) {
            refetchData();
        }
    };

    useEffect(() => {
        fetchByStudentId(studentId, { page: 1, limit: 10 });
    }, [studentId]);


    return (
        <div className="page-wrapper">
            <div className="content container-fluid">
                <PageHeader title={t("authorizedPickUpPerson.title")} breadcrumbItems={breadcrumbItems} />

                <>
                    <StudentSelectorWrapper rightSlot={<div className="d-flex gap-2">

                        <TableFilterDynamique
                            fields={filterFields}
                            filters={filters}
                            onApply={handleFilter}
                            onReset={() => handleFilter({ page: 1, limit: 10 })}
                        />
                        <CustomButton
                            onClick={handleAddNew}
                        >
                            <i className="ti ti-plus me-2" />
                            {t("authorizedPickUpPerson.add")}
                        </CustomButton>
                    </div>}>


                        <AuthorizedPickUpPersonTable
                            dataSource={authorizedPickUpPersons}
                            loading={loading}
                            onEdit={handleEdit}
                            onDelete={handleDeletePickUpPerson}
                            dataPaginate={{
                                current: infoPagination?.page,
                                pageSize: infoPagination?.limit,
                                total: infoPagination?.total,
                            }}
                            onChange={(pg) => {
                                const params = {
                                    ...filters,
                                    page: pg.current!,
                                    limit: pg.pageSize!,
                                };

                                fetchByStudentId(studentId, params);
                            }}
                        />

                        <AddEditAuthorizedPickUpPerson
                            isOpen={isModalOpen}
                            onClose={handleCloseModal}
                            pickUpPersonData={selectedPickUpPerson}
                            onSave={handleSave}
                        />
                    </StudentSelectorWrapper>
                </>

            </div>
        </div>
    );
};

export default AuthorizedPickUpPersonPage;